import { normalizeEmbedOrigin } from './bootstrap';
import {
  isEmbedChannelId,
  parseEmbedHostMessage,
  parseEmbedPlayerMessage,
  type EmbedHostMessage,
  type EmbedPlayerMessage,
} from './protocol';

export interface EmbedMessageEventTarget {
  addEventListener(type: 'message', listener: (event: MessageEvent<unknown>) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent<unknown>) => void): void;
}

export interface EmbedPostTarget {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface SecureEmbedBridge<TIncoming, TOutgoing> {
  readonly remoteOrigin: string;
  readonly channelId: string;
  send(message: TOutgoing): void;
  subscribe(listener: (message: TIncoming) => void): () => void;
  destroy(): void;
}

interface BridgeOptions<TIncoming, TOutgoing> {
  readonly eventTarget: EmbedMessageEventTarget;
  readonly remoteWindow: EmbedPostTarget;
  readonly expectedSource?: unknown;
  readonly remoteOrigin: string;
  readonly channelId: string;
  readonly parseIncoming: (value: unknown) => TIncoming | null;
  readonly readChannelId: (message: TIncoming | TOutgoing) => string;
  readonly readMessageId: (message: TIncoming) => string;
}

const maxRememberedIncomingMessageIds = 256;

function createSecureBridge<TIncoming, TOutgoing>(
  options: BridgeOptions<TIncoming, TOutgoing>,
): SecureEmbedBridge<TIncoming, TOutgoing> {
  const remoteOrigin = normalizeEmbedOrigin(options.remoteOrigin);
  if (!isEmbedChannelId(options.channelId)) throw new RangeError('Invalid embed channel id.');

  const listeners = new Set<(message: TIncoming) => void>();
  const expectedSource = options.expectedSource ?? options.remoteWindow;
  const seenIncomingMessageIds = new Set<string>();
  const incomingMessageOrder: string[] = [];
  let destroyed = false;

  const onMessage = (event: MessageEvent<unknown>) => {
    if (destroyed) return;
    if (event.origin !== remoteOrigin || event.source !== expectedSource) return;

    const message = options.parseIncoming(event.data);
    if (!message || options.readChannelId(message) !== options.channelId) return;

    const messageId = options.readMessageId(message);
    if (seenIncomingMessageIds.has(messageId)) return;

    seenIncomingMessageIds.add(messageId);
    incomingMessageOrder.push(messageId);
    if (incomingMessageOrder.length > maxRememberedIncomingMessageIds) {
      const expiredMessageId = incomingMessageOrder.shift();
      if (expiredMessageId) seenIncomingMessageIds.delete(expiredMessageId);
    }

    for (const listener of listeners) listener(message);
  };

  options.eventTarget.addEventListener('message', onMessage);

  return {
    remoteOrigin,
    channelId: options.channelId,
    send(message) {
      if (destroyed) throw new Error('Embed bridge has been destroyed.');
      if (options.readChannelId(message) !== options.channelId) {
        throw new RangeError('Embed message channel does not match the bridge channel.');
      }
      options.remoteWindow.postMessage(message, remoteOrigin);
    },
    subscribe(listener) {
      if (destroyed) throw new Error('Embed bridge has been destroyed.');
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      listeners.clear();
      seenIncomingMessageIds.clear();
      incomingMessageOrder.length = 0;
      options.eventTarget.removeEventListener('message', onMessage);
    },
  };
}

export interface HostEmbedBridgeOptions {
  readonly eventTarget: EmbedMessageEventTarget;
  readonly playerWindow: EmbedPostTarget;
  readonly expectedPlayerSource?: unknown;
  readonly playerOrigin: string;
  readonly channelId: string;
}

export function createHostEmbedBridge(
  options: HostEmbedBridgeOptions,
): SecureEmbedBridge<EmbedPlayerMessage, EmbedHostMessage> {
  return createSecureBridge({
    eventTarget: options.eventTarget,
    remoteWindow: options.playerWindow,
    ...(options.expectedPlayerSource === undefined
      ? {}
      : { expectedSource: options.expectedPlayerSource }),
    remoteOrigin: options.playerOrigin,
    channelId: options.channelId,
    parseIncoming: parseEmbedPlayerMessage,
    readChannelId: (message) => message.channelId,
    readMessageId: (message) => message.messageId,
  });
}

export interface PlayerEmbedBridgeOptions {
  readonly eventTarget: EmbedMessageEventTarget;
  readonly hostWindow: EmbedPostTarget;
  readonly expectedHostSource?: unknown;
  readonly hostOrigin: string;
  readonly channelId: string;
}

export function createPlayerEmbedBridge(
  options: PlayerEmbedBridgeOptions,
): SecureEmbedBridge<EmbedHostMessage, EmbedPlayerMessage> {
  return createSecureBridge({
    eventTarget: options.eventTarget,
    remoteWindow: options.hostWindow,
    ...(options.expectedHostSource === undefined
      ? {}
      : { expectedSource: options.expectedHostSource }),
    remoteOrigin: options.hostOrigin,
    channelId: options.channelId,
    parseIncoming: parseEmbedHostMessage,
    readChannelId: (message) => message.channelId,
    readMessageId: (message) => message.messageId,
  });
}
